import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }

  .subheading {
    font-size: var(--fz-xl);
    font-weight: 600;
    color: var(--lightest-slate);
    margin: 45px 0 20px;
    display: flex;
    align-items: center;

    &:before {
      content: '▹';
      margin-right: 10px;
      color: var(--green);
      font-family: var(--font-mono);
    }
  }

  .categories-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 16px;
    margin-top: 20px;
  }

  .category-card {
    background-color: var(--light-navy);
    padding: 18px 20px;
    border-radius: var(--border-radius);
    border: 1px solid rgba(255, 255, 255, 0.05);
    transition: var(--transition);

    &:hover {
      transform: translateY(-3px);
      border-color: rgba(100, 255, 218, 0.4);
      box-shadow: 0 10px 30px -15px var(--navy-shadow);
    }

    h4 {
      font-family: var(--font-mono);
      font-size: var(--fz-xs);
      color: var(--green);
      margin: 0 0 12px 0;
      font-weight: 600;
      letter-spacing: 0.5px;
    }

    ul {
      padding: 0;
      margin: 0;
      list-style: none;
      display: flex;
      flex-wrap: wrap;
      gap: 6px;

      li {
        font-family: var(--font-mono);
        font-size: var(--fz-xxs);
        color: var(--light-slate);
        background-color: var(--navy);
        padding: 4px 8px;
        border-radius: var(--border-radius);
        border: 1px solid rgba(100, 255, 218, 0.1);
        transition: var(--transition);

        &:hover {
          color: var(--green);
          border-color: var(--green);
        }
      }
    }
  }

  .competencias-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
    gap: 12px;
    margin-top: 20px;

    .competencia-item {
      display: flex;
      align-items: center;
      font-size: var(--fz-sm);
      color: var(--light-slate);
      background-color: var(--light-navy);
      padding: 12px 16px;
      border-radius: var(--border-radius);
      border: 1px solid rgba(255, 255, 255, 0.04);
      transition: var(--transition);

      &:hover {
        color: var(--white);
        border-color: rgba(100, 255, 218, 0.3);
        transform: translateY(-2px);
      }

      &:before {
        content: '▹';
        color: var(--green);
        margin-right: 12px;
        font-family: var(--font-mono);
        font-size: var(--fz-sm);
        flex-shrink: 0;
      }
    }
  }
`;

const StyledText = styled.div``;

const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      outline: 0;

      &:after {
        top: 15px;
        left: 15px;
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 20px;
      left: 20px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const techCategories = [
    {
      title: 'Lenguajes de programación',
      skills: ['JavaScript (ES6+)', 'TypeScript', 'PHP', 'Python', 'Swift', 'HTML5', 'CSS3', 'SQL'],
    },
    {
      title: 'Frontend',
      skills: ['React', 'React Native', 'Bootstrap', 'jQuery', 'Responsive Design'],
    },
    {
      title: 'Backend',
      skills: ['Node.js', 'Express.js', 'REST APIs', 'JSON', 'XML'],
    },
    {
      title: 'Inteligencia Artificial',
      skills: [
        'OpenAI API',
        'Claude API',
        'Google Gemini API',
        'Prompt Engineering',
        'AI Agents',
        'MCP (Model Context Protocol)',
        'n8n',
        'Automatización de procesos con IA',
        'Integración de modelos LLM',
      ],
    },
    {
      title: 'Cloud & Infraestructura',
      skills: [
        'AWS',
        'Vercel',
        'Cloudflare',
        'Cloudways',
        'GitHub',
        'Git',
        'SSH',
        'cPanel',
        'Linux Server Administration',
      ],
    },
    {
      title: 'Bases de datos',
      skills: ['MySQL', 'MongoDB', 'Microsoft SQL Server'],
    },
    {
      title: 'CMS / Ecommerce',
      skills: ['WordPress', 'WooCommerce', 'Magento', 'PrestaShop', 'Joomla', 'Drupal'],
    },
    {
      title: 'Ecosistema WordPress',
      skills: [
        'Divi',
        'Advanced Custom Fields (ACF Pro)',
        'Divi Machine',
        'Custom Post Types',
        'Hooks & Filters',
        'Child Themes',
      ],
    },
    {
      title: 'Optimización y Rendimiento',
      skills: ['Optimización de rendimiento', 'SEO Técnico'],
    },
    {
      title: 'CRM',
      skills: ['Microsoft Dynamics CRM', 'HubSpot', 'Zoho CRM', 'Nabuco CRM'],
    },
    {
      title: 'ERP',
      skills: ['Microsoft Dynamics NAV 2017 (Navision)', 'Nabuco ERP'],
    },
    {
      title: 'Ecosistema Microsoft',
      skills: [
        'Microsoft 365',
        'Outlook',
        'Excel',
        'Word',
        'PowerPoint',
        'Teams',
        'OneDrive',
        'SharePoint',
        'Microsoft Clarity',
      ],
    },
    {
      title: 'Ecosistema Google',
      skills: [
        'Google Analytics 4 (GA4)',
        'Google Search Console',
        'Google Tag Manager',
        'Google Ads',
        'Google Workspace',
        'Google Business Profile',
      ],
    },
    {
      title: 'Marketing Digital',
      skills: ['Meta Ads', 'Mailchimp', 'SEO', 'SEM', 'Email Marketing', 'Conversión y Analítica Web'],
    },
    {
      title: 'Diseño UI/UX',
      skills: ['Figma', 'Sketch', 'Adobe Photoshop', 'Adobe Illustrator', 'Adobe Lightroom'],
    },
    {
      title: 'Herramientas de Desarrollo',
      skills: [
        'Visual Studio Code',
        'Cursor AI',
        'GitHub Copilot',
        'Postman',
        'Notion',
        'Jira',
        'Trello',
        'Slack',
        'npm',
        'Composer',
      ],
    },
    {
      title: 'Sistemas Operativos',
      skills: ['macOS', 'Linux', 'Windows'],
    },
  ];

  const competencias = [
    'Arquitectura de software',
    'Diseño de aplicaciones SaaS',
    'Desarrollo de aplicaciones móviles (iOS y React Native)',
    'Integración de APIs y servicios de terceros',
    'Automatización de procesos empresariales',
    'Optimización SEO y rendimiento web',
    'Gestión de proyectos tecnológicos',
    'Diseño de bases de datos',
    'Desarrollo de plataformas e-commerce',
    'Implementación de soluciones de IA generativa',
    'Desarrollo de agentes inteligentes y flujos automatizados',
    'Análisis de datos y métricas de negocio',
    'UX/UI orientado a conversión',
  ];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">Sobre mí</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
              Hola👋, soy Dayron Hernandez, Estratega Tecnológico y Arquitecto de Software. Con más de 8 años de experiencia en el sector tech, me especializo en cerrar la brecha entre los objetivos de negocio y la ejecución técnica.
            </p>
            <p>
              En mi etapa como CTO en DomesticoShop, lideré la visión tecnológica y la integración de ecosistemas complejos. Mi enfoque no es solo escribir código, sino diseñar soluciones que optimicen procesos y generen valor real tanto para startups como para grandes corporaciones.
            </p>

            <p>
              Me apasiona detectar necesidades latentes en las organizaciones y ofrecer soluciones integrales que abarcan desde la arquitectura de software hasta la formación de equipos no técnicos, asegurando una adopción tecnológica fluida y eficiente.
            </p>
          </div>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpeg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>

      <h3 className="subheading">Tecnologías y Stack Técnico</h3>

      <div className="categories-grid">
        {techCategories.map((cat, i) => (
          <div className="category-card" key={i}>
            <h4>{cat.title}</h4>
            <ul>
              {cat.skills.map((skill, j) => (
                <li key={j}>{skill}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <h3 className="subheading">Competencias y Habilidades Clave</h3>

      <div className="competencias-grid">
        {competencias.map((comp, i) => (
          <div className="competencia-item" key={i}>
            {comp}
          </div>
        ))}
      </div>
    </StyledAboutSection>
  );
};

export default About;
